import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  Maximize2,
  MessageSquare,
  Minimize2,
  PanelRightClose,
  Minus,
  Plus,
} from "lucide-react";
import { documentEditorWindowUrl } from "./document-editor-window";
import { api } from "../../api";
import { DocumentEditorContext } from "./DocumentEditorContext";
import { beginPointerResize } from "../shared/pointer-resize";
import {
  DOCUMENT_CHAT_DEFAULT_WIDTH,
  DOCUMENT_MIN_WIDTH,
  DOCUMENT_CHAT_MIN_WIDTH,
  documentEditorLayout,
} from "./document-editor-layout";

const DocumentEditorPanel = lazy(() =>
  import("./DocumentEditorPanel").then((module) => ({
    default: module.DocumentEditorPanel,
  })),
);

function preference(name: string, fallback: string) {
  try {
    return localStorage.getItem(`lumen:document-${name}`) ?? fallback;
  } catch {
    return fallback;
  }
}

export function DocumentEditorProvider({
  conversationId,
  onAddToChat,
  onToggleSidebar,
  initialArtifactId,
  children,
}: {
  conversationId?: string;
  initialArtifactId?: string;
  onAddToChat: (text: string) => void;
  onToggleSidebar?: () => void;
  children: ReactNode;
}) {
  const [enabled, setEnabled] = useState(false);
  const [artifactId, setArtifactId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [chatOpen, setChatOpen] = useState(
    () => preference("chat-open", "false") === "true",
  );
  const [chatWidth, setChatWidth] = useState(() =>
    Number(preference("chat-width", String(DOCUMENT_CHAT_DEFAULT_WIDTH))),
  );
  const [availableWidth, setAvailableWidth] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [canFullscreen, setCanFullscreen] = useState(false);
  const editorWindows = useRef(new Map<string, Window>());
  const root = useRef<HTMLDivElement>(null);
  const conversationPane = useRef<HTMLDivElement>(null);
  const backToDocument = useRef<HTMLButtonElement>(null);
  const conversationToggle = useRef<HTMLButtonElement>(null);
  const opened = Boolean(artifactId && conversationId);
  const {
    split,
    chatOnly,
    chatWidth: visibleChatWidth,
    maximum,
  } = documentEditorLayout(availableWidth, chatWidth, chatOpen);

  useEffect(() => {
    let active = true;
    void api<{ enabled: boolean }>("/document-editor/status")
      .then((result) => {
        if (active) setEnabled(result.enabled);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setArtifactId(initialArtifactId || null);
    setNotice("");
  }, [conversationId, initialArtifactId]);
  useEffect(() => {
    if (!root.current) return;
    const observer = new ResizeObserver(([entry]) =>
      setAvailableWidth(entry.contentRect.width),
    );
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem("lumen:document-chat-open", String(chatOpen));
      localStorage.setItem("lumen:document-chat-width", String(chatWidth));
    } catch {
      /* Preferences are optional. */
    }
  }, [chatOpen, chatWidth]);
  useEffect(() => {
    const changed = () =>
      setFullscreen(document.fullscreenElement === root.current);
    setCanFullscreen(
      document.fullscreenEnabled && Boolean(root.current?.requestFullscreen),
    );
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, []);

  const resize = (value: number) =>
    setChatWidth(Math.max(DOCUMENT_CHAT_MIN_WIDTH, Math.min(maximum, value)));
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === root.current)
        await document.exitFullscreen();
      else await root.current?.requestFullscreen();
    } catch {
      setNotice(
        "Fullscreen is unavailable. You can hide the conversation or the sidebar to give the document more room.",
      );
    }
  };
  const showConversation = (focusComposer = false) => {
    setChatOpen(true);
    requestAnimationFrame(() => {
      if (focusComposer)
        conversationPane.current
          ?.querySelector<HTMLElement>('[contenteditable="true"], textarea')
          ?.focus();
      else if (!split) backToDocument.current?.focus();
    });
  };
  const hideConversation = () => {
    setChatOpen(false);
    requestAnimationFrame(() => conversationToggle.current?.focus());
  };
  const controls = (
    <>
      <button
        type="button"
        className="button min-h-11 gap-1.5 px-3 text-xs text-stone-700 dark:text-stone-200"
        ref={conversationToggle}
        aria-pressed={chatOpen}
        onClick={() => (chatOpen ? hideConversation() : showConversation())}
        title={chatOpen ? "Hide conversation" : "Show conversation"}
      >
        <MessageSquare size={16} aria-hidden="true" /> Chat
      </button>
      {canFullscreen && (
        <button
          type="button"
          className="button grid size-11 place-items-center text-stone-700 dark:text-stone-200"
          aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          title={fullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
          onClick={() => void toggleFullscreen()}
        >
          {fullscreen ? (
            <Minimize2 size={16} aria-hidden="true" />
          ) : (
            <Maximize2 size={16} aria-hidden="true" />
          )}
        </button>
      )}
    </>
  );

  return (
    <DocumentEditorContext.Provider
      value={{
        enabled: enabled && Boolean(conversationId),
        active: opened,
        openInWindow: (id) => {
          if (!conversationId) return;
          const existing = editorWindows.current.get(id);
          if (existing && !existing.closed) {
            existing.focus();
            return;
          }
          if (artifactId) {
            setNotice(
              "Save & close the current document before opening an editor window.",
            );
            return;
          }
          const popup = window.open(
            documentEditorWindowUrl(
              conversationId,
              id,
              window.location.pathname,
            ),
            `lumen-document-${conversationId}-${id}`,
            "popup,width=1440,height=960,resizable=yes,scrollbars=yes",
          );
          if (popup) {
            editorWindows.current.set(id, popup);
            popup.opener = null;
            popup.focus();
          } else {
            setNotice(
              "Allow popups for this site to open the editor in a new window.",
            );
          }
        },
        open: (id) => {
          if (artifactId && artifactId !== id) {
            setNotice(
              "Save & close the current document before opening another.",
            );
            return;
          }
          setNotice("");
          setArtifactId(id);
          // Opening from a narrow conversation view always takes you to the document.
          if (availableWidth < DOCUMENT_MIN_WIDTH + DOCUMENT_CHAT_MIN_WIDTH)
            setChatOpen(false);
        },
      }}
    >
      <div
        ref={root}
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-[#171b1a]"
        data-document-workspace={opened ? "open" : undefined}
      >
        {!opened && notice && (
          <p
            role="status"
            className="shrink-0 border-b border-stone-200 px-4 py-2 text-sm dark:border-white/10"
          >
            {notice}
          </p>
        )}
        {opened && chatOnly && (
          <div className="flex min-h-12 shrink-0 items-center gap-2 border-b border-stone-200 px-3 py-1 dark:border-white/10">
            <button
              ref={backToDocument}
              type="button"
              className="button min-h-11 gap-1.5 px-3 text-xs text-stone-700 dark:text-stone-200"
              onClick={hideConversation}
            >
              <ArrowLeft size={16} aria-hidden="true" /> Back to document
            </button>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              Conversation
            </span>
            {canFullscreen && (
              <button
                type="button"
                className="button grid size-11 place-items-center text-stone-700 dark:text-stone-200"
                aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                onClick={() => void toggleFullscreen()}
              >
                {fullscreen ? (
                  <Minimize2 size={16} aria-hidden="true" />
                ) : (
                  <Maximize2 size={16} aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        )}
        <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {/* Stable siblings keep both the editor session and chat draft alive. */}
          <div
            ref={conversationPane}
            hidden={opened && !chatOpen}
            className={`${opened && !chatOpen ? "hidden" : "flex"} min-h-0 min-w-0 flex-col overflow-hidden ${opened && split ? "relative order-2 shrink-0 border-l border-stone-200 dark:border-white/10" : "flex-1"}`}
            style={opened && split ? { width: visibleChatWidth } : undefined}
            data-document-conversation=""
          >
            {opened && split && (
              <>
                <button
                  type="button"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize conversation panel"
                  aria-valuemin={DOCUMENT_CHAT_MIN_WIDTH}
                  aria-valuemax={maximum}
                  aria-valuenow={Math.round(visibleChatWidth)}
                  title="Drag to resize, or use arrow keys"
                  onPointerDown={(event) =>
                    beginPointerResize(event, {
                      axis: "x",
                      cursor: "col-resize",
                      initialValue: visibleChatWidth,
                      minimum: DOCUMENT_CHAT_MIN_WIDTH,
                      maximum,
                      onValueChange: resize,
                    })
                  }
                  onKeyDown={(event) => {
                    if (
                      ["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                        event.key,
                      )
                    ) {
                      event.preventDefault();
                      resize(
                        event.key === "Home"
                          ? DOCUMENT_CHAT_MIN_WIDTH
                          : event.key === "End"
                            ? maximum
                            : visibleChatWidth +
                              (event.key === "ArrowLeft" ? 24 : -24),
                      );
                    }
                  }}
                  className="absolute inset-y-0 -left-1.5 z-20 w-3 cursor-col-resize touch-none hover:bg-lumen-300/30 focus-visible:bg-lumen-300/50"
                />
                <div className="flex min-h-12 shrink-0 items-center gap-1 border-b border-stone-200 px-3 dark:border-white/10">
                  <span className="min-w-0 flex-1 text-xs font-semibold">
                    Conversation
                  </span>
                  <button
                    type="button"
                    className="grid size-11 place-items-center rounded-lg text-stone-600 hover:bg-stone-100 disabled:opacity-40 dark:text-stone-300 dark:hover:bg-white/10"
                    aria-label="Narrower conversation"
                    disabled={visibleChatWidth <= DOCUMENT_CHAT_MIN_WIDTH}
                    onClick={() => resize(visibleChatWidth - 40)}
                  >
                    <Minus size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="grid size-11 place-items-center rounded-lg text-stone-600 hover:bg-stone-100 disabled:opacity-40 dark:text-stone-300 dark:hover:bg-white/10"
                    aria-label="Wider conversation"
                    disabled={visibleChatWidth >= maximum}
                    onClick={() => resize(visibleChatWidth + 40)}
                  >
                    <Plus size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="grid size-11 place-items-center rounded-lg text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-white/10"
                    aria-label="Hide conversation"
                    onClick={hideConversation}
                  >
                    <PanelRightClose size={16} aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
            {children}
          </div>
          {opened && (
            <aside
              aria-label="Document editor"
              hidden={chatOnly}
              className={`${chatOnly ? "hidden" : "flex"} order-1 min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-[#171b1a]`}
            >
              {notice && (
                <p
                  role="status"
                  className="shrink-0 border-b border-stone-200 px-4 py-2 text-sm dark:border-white/10"
                >
                  {notice}
                </p>
              )}
              <Suspense
                fallback={
                  <p className="p-4" role="status">
                    Loading document editor…
                  </p>
                }
              >
                <DocumentEditorPanel
                  key={`${conversationId}:${artifactId}`}
                  artifactId={artifactId!}
                  conversationId={conversationId!}
                  onToggleSidebar={fullscreen ? undefined : onToggleSidebar}
                  workspaceControls={controls}
                  onAddToChat={(text) => {
                    onAddToChat(text);
                    showConversation(true);
                  }}
                  onClose={() => {
                    if (document.fullscreenElement === root.current)
                      void document.exitFullscreen().catch(() => {});
                    setArtifactId(null);
                    setNotice("");
                  }}
                />
              </Suspense>
            </aside>
          )}
        </div>
      </div>
    </DocumentEditorContext.Provider>
  );
}
